import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Check, Search } from 'lucide-react';

import { PlateformeDto } from 'api/plateforme';
import { GenericLoading } from 'components/ui/Loader';
import { NsKey } from 'enums/NsKey';
import { useDialog } from 'hooks/useDialog';
import { plateformesAPI } from 'services/swagger-api';

interface PlateformesPickerFormProps {
  /** Restrict the list to these plateforme codes (e.g. per-context filtering). */
  includeCodes?: string[];
  initialSelectedCodes?: string[];
  /** Persists the new FULL selection (the link commands replace the set). */
  onSubmit: (selectedCodes: string[]) => Promise<void>;
  onSuccess?: () => void;
}

/**
 * Dialog body to pick plateformes: compact cards (logo + name), click to
 * toggle, selected cards highlighted, search on top — built for lists with
 * many plateformes. Same code-based contract as the old tag form so every
 * caller (tunnels, products, personnes morales) plugs in unchanged.
 */
export const PlateformesPickerForm: React.FC<PlateformesPickerFormProps> = ({
  includeCodes,
  initialSelectedCodes = [],
  onSubmit,
  onSuccess
}) => {
  const { t } = useTranslation(NsKey.Common);
  const { setDialogActions, closeDialog } = useDialog();

  const [plateformes, setPlateformes] = useState<PlateformeDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(initialSelectedCodes)
  );

  useEffect(() => {
    let cancelled = false;
    plateformesAPI
      .getPlateformes()
      .then(res => {
        if (cancelled) return;
        const all = res.plateformes ?? [];
        const filtered = includeCodes
          ? all.filter(p => includeCodes.includes(p.code))
          : all;
        setPlateformes(
          [...filtered].sort((a, b) => a.name.localeCompare(b.name))
        );
      })
      .catch(() => {
        if (!cancelled) setPlateformes([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // includeCodes is compared by content, not identity, by the callers' usage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = useCallback((code: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    setSaving(true);
    try {
      await onSubmit(Array.from(selected));
      onSuccess?.();
      closeDialog();
    } catch (error) {
      console.error('Error on plateformes link submit:', error);
    } finally {
      setSaving(false);
    }
  }, [onSubmit, onSuccess, selected, closeDialog]);

  useEffect(() => {
    setDialogActions({
      closeLabel: 'COMMON.CANCEL',
      submitLabel: 'COMMON.VALIDATE',
      submitDisabled: selected.size === 0 || saving || loading,
      onSubmit: handleSubmit
    });
  }, [setDialogActions, selected.size, saving, loading, handleSubmit]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return plateformes;
    return plateformes.filter(
      p =>
        p.name.toLowerCase().includes(needle) ||
        p.code.toLowerCase().includes(needle)
    );
  }, [plateformes, search]);

  if (loading) {
    return <GenericLoading loading />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1">
          <Search
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('PLATEFORME_PICKER.SEARCH')}
            className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        <span className="flex-shrink-0 text-sm text-gray-500">
          {t('PLATEFORME_PICKER.SELECTED_COUNT', { count: selected.size })}
        </span>
      </div>

      {visible.length === 0 ? (
        <p className="py-10 text-center text-sm text-gray-500">
          {t('PLATEFORME_PICKER.EMPTY')}
        </p>
      ) : (
        <div className="grid max-h-[55vh] grid-cols-2 gap-3 overflow-y-auto p-1 sm:grid-cols-3">
          {visible.map(plateforme => {
            const isSelected = selected.has(plateforme.code);
            const logoUrl =
              plateforme.theme?.minimalLogoUrl || plateforme.theme?.logoUrl;
            return (
              <button
                key={plateforme.id}
                type="button"
                aria-pressed={isSelected}
                onClick={() => toggle(plateforme.code)}
                className={`relative flex items-center gap-3 rounded-xl border p-3 text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                  isSelected
                    ? 'border-primary bg-primary/5 ring-1 ring-primary'
                    : 'border-gray-200 bg-white hover:border-primary/40 hover:bg-primary/[0.02]'
                }`}
              >
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border bg-white p-1">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt=""
                      className="max-h-full max-w-full object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <Building2 size={16} className="text-gray-400" />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-gray-900">
                    {plateforme.name}
                  </span>
                  <span className="block truncate text-xs text-gray-400">
                    {plateforme.code}
                  </span>
                </span>
                {isSelected && (
                  <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary text-white">
                    <Check size={12} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
